import { FC, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useTasksControllerGenerateOutputMutation } from 'src/app/services/api.generated';
import { useLazyTasksControllerGetTaskOutputStateQuery } from 'src/app/services/api';
import { isGeneratingOutputState } from 'src/utils/taskProgressStreamUtils';
import { zgwReasonKeys } from 'src/app/services/zgwErrors';
import { zgwErrorCode, useZgwStatusQuery, useZgwTaskQuery, useZgwWriteBackMutation } from 'src/app/services/zgwApi';

type Props = {
  taskId?: string;
  /** The task has a finished export (the redacted PDF exists). */
  hasExport: boolean;
  /** How often the export is polled after an automatic re-export. */
  pollMs?: number;
};

/** Give up waiting for the re-export after this many polls. */
const MAX_POLLS = 150;

const ERROR_KEYS: Record<string, string> = {
  linkFailed: 'zgw.writeBack.partial',
  alreadyWrittenBack: 'zgw.writeBack.already',
  noExport: 'zgw.writeBack.noExport',
  writeBackInProgress: 'zgw.writeBack.inProgress',
  connectionChanged: 'zgw.writeBack.connectionChanged',
  zgwNotConfigured: 'zgw.import.notConfigured',
  zgwRequestFailed: 'zgw.import.requestFailed',
  tooLarge: 'zgw.import.tooLarge',
  exportOutdated: 'zgw.writeBack.exportStillOutdated',
};

/**
 * "Write back to case": only for a task imported from a case that has an
 * export, and only while the company's connection is enabled. Explicit
 * action with a confirmation that says exactly what happens.
 */
export const ZgwWriteBackButton: FC<Props> = ({ taskId, hasExport, pollMs = 2000 }) => {
  const { t } = useTranslation();
  const { data: status } = useZgwStatusQuery();
  const enabled = !!status?.enabled;
  const { data: info } = useZgwTaskQuery({ taskId: taskId ?? '' }, { skip: !taskId || !enabled });
  const [writeBack, { isLoading }] = useZgwWriteBackMutation();
  const [generateOutput] = useTasksControllerGenerateOutputMutation();
  const [getOutputState] = useLazyTasksControllerGetTaskOutputStateQuery();
  const [confirm, setConfirm] = useState(false);
  /** Re-exporting before the write-back. */
  const [reexporting, setReexporting] = useState(false);

  if (!taskId || !enabled || !info?.imported || !hasExport) return null;

  if (info.writeBackState === 'linked') {
    return (
      <Typography variant="caption" color="text.secondary" data-testid="zgw-written-back" sx={{ whiteSpace: 'nowrap' }}>
        {t('zgw.writeBack.done')}
      </Typography>
    );
  }

  const partial = info.writeBackState === 'created';

  /** Re-export and wait for the new PDF. False when it did not finish. */
  const reexport = async (): Promise<boolean> => {
    await generateOutput({ generateOutputDto: { id: taskId } }).unwrap();
    for (let i = 0; i < MAX_POLLS; i += 1) {
      const { outputState } = await getOutputState({ id: taskId }, false).unwrap();
      if (outputState === 'GENERATED_OUTPUT') return true;
      if (!isGeneratingOutputState(outputState)) return false;
      await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
    return false;
  };

  const fail = (err: unknown) => {
    const code = zgwErrorCode(err);
    const key = (code && ERROR_KEYS[code]) || 'somethingWentWrongTryAgain';
    if (code === 'linkFailed') {
      toast.warning(t(key));
      // Why the link was refused, when known (nl only for now).
      const [reasonKey] = zgwReasonKeys(err, '');
      if (reasonKey) toast.error(t(reasonKey));
    } else if (code === 'zgwRequestFailed') toast.error(t(zgwReasonKeys(err, key)));
    else toast.error(t(key));
  };

  const onConfirm = async () => {
    setConfirm(false);
    try {
      try {
        await writeBack({ taskId }).unwrap();
      } catch (err) {
        // The server refused a stale export: generate it again, once.
        if (zgwErrorCode(err) !== 'exportOutdated') throw err;
        toast.info(t('zgw.writeBack.exportOutdated'));
        setReexporting(true);
        if (!(await reexport())) {
          toast.error(t('generateOutputFailed'));
          return;
        }
        await writeBack({ taskId }).unwrap();
      }
      toast.success(t('zgw.writeBack.success'));
    } catch (err) {
      fail(err);
    } finally {
      setReexporting(false);
    }
  };

  return (
    <>
      <Tooltip title={partial ? t('zgw.writeBack.partial') : ''}>
        <span>
          <LoadingButton
            size="small"
            variant="outlined"
            color={partial ? 'warning' : 'primary'}
            loading={isLoading || reexporting}
            onClick={() => setConfirm(true)}
            data-testid="zgw-write-back"
            sx={{ flexShrink: 0 }}
          >
            {partial ? t('zgw.writeBack.retry') : t('zgw.writeBack.button')}
          </LoadingButton>
        </span>
      </Tooltip>
      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>{t('zgw.writeBack.button')}</DialogTitle>
        <DialogContent>
          <Typography data-testid="zgw-write-back-confirm">{partial ? t('zgw.writeBack.confirmRetry') : t('zgw.writeBack.confirm', { titel: info.writeBackTitel ?? '' })}</Typography>
          {!partial && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('zgw.writeBack.confirmConfidentiality', { level: info.vertrouwelijkheidaanduiding ?? '' })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={onConfirm}>
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ZgwWriteBackButton;
